import { useEffect, useId, useState } from "react"

import { createPortal } from "react-dom"

import { PINNED_BATTERY_PROPOSAL_THEME } from "../BatteryOnlyProposal/theme/battery-proposal-theme"

/** Above the Setup rail and step drawers, which share z-index 2147483000. */
const SEARCH_LAYER_Z = 2147483001

/** One row of the palette. A locked row explains itself and cannot open. */
export type ProposalSetupSearchOption = {
  readonly id: string
  readonly label: string
  readonly groupLabel: string
  readonly lockedHint: string | null
}

/**
 * The ⌘K palette shell: a portal dialog with a focused search input over a
 * listbox. The proposal owns its entries and matching; this owns keyboard
 * movement, the empty state, and the layer.
 */
export const ProposalSetupQuickSearch = <
  Option extends ProposalSetupSearchOption,
>({
  alert,
  emptyLabel,
  matches,
  onActivate,
  onClose,
  query,
  setQuery,
  testId,
}: {
  readonly alert: string | null
  readonly emptyLabel: string
  readonly matches: readonly Option[]
  readonly onActivate: (option: Option) => void
  readonly onClose: () => void
  readonly query: string
  readonly setQuery: (query: string) => void
  readonly testId: string
}): JSX.Element | null => {
  const listId = useId()
  const [activeIndex, setActiveIndex] = useState(0)
  const active = matches[activeIndex] ?? null

  useEffect(() => {
    setActiveIndex(0)
  }, [query])

  if (typeof document === `undefined`) {
    return null
  }

  const optionId = (option: Option): string => `${listId}-${option.id}`

  return createPortal(
    <div
      data-battery-theme={PINNED_BATTERY_PROPOSAL_THEME}
      className="fixed inset-0 flex items-start justify-center bg-black/50 pt-[12vh] print:hidden"
      style={{ zIndex: SEARCH_LAYER_Z }}
    >
      <button
        type="button"
        aria-label="Dismiss search"
        className="absolute inset-0 cursor-default"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-label="Search setup"
        data-testid={testId}
        className="relative z-[1] flex w-[min(32rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-lg border border-bp-border-strong bg-bp-elevated shadow-xl"
      >
        <input
          autoFocus
          role="combobox"
          aria-expanded
          aria-controls={listId}
          aria-activedescendant={active ? optionId(active) : undefined}
          aria-autocomplete="list"
          placeholder="Search setup"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === `ArrowDown`) {
              event.preventDefault()
              setActiveIndex((index) =>
                matches.length === 0 ? 0 : (index + 1) % matches.length,
              )
              return
            }
            if (event.key === `ArrowUp`) {
              event.preventDefault()
              setActiveIndex((index) =>
                matches.length === 0
                  ? 0
                  : (index - 1 + matches.length) % matches.length,
              )
              return
            }
            if (event.key === `Enter` && active) {
              event.preventDefault()
              onActivate(active)
            }
          }}
          className="font-display w-full border-b border-bp-border-strong bg-transparent px-4 py-3 text-[0.9375rem] text-bp-text outline-none placeholder:text-bp-text-dim"
        />
        <ul
          id={listId}
          role="listbox"
          aria-label="Setup"
          className="max-h-[min(24rem,50vh)] list-none overflow-y-auto py-1"
        >
          {matches.length === 0 ? (
            <li className="px-4 py-3 text-[0.875rem] text-bp-text-dim">
              {emptyLabel}
            </li>
          ) : (
            matches.map((option, index) => {
              const locked = option.lockedHint != null
              const selected = index === activeIndex
              return (
                <li key={option.id} role="presentation">
                  <button
                    type="button"
                    id={optionId(option)}
                    role="option"
                    aria-selected={selected}
                    aria-disabled={locked}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => onActivate(option)}
                    className={`font-display flex w-full flex-col items-start px-4 py-2 text-left text-[0.875rem] leading-[21px] ${
                      selected ? `bg-bp-elevated-2` : `bg-transparent`
                    } ${locked ? `cursor-not-allowed text-bp-text-dim` : `text-bp-text`}`}
                  >
                    <span>{option.label}</span>
                    {option.label === option.groupLabel ? null : (
                      <span className="text-[0.75rem] leading-tight text-bp-text-muted">
                        {option.groupLabel}
                      </span>
                    )}
                    {locked ? (
                      <span className="text-[0.75rem] leading-tight text-bp-text-muted">
                        {option.lockedHint}
                      </span>
                    ) : null}
                  </button>
                </li>
              )
            })
          )}
        </ul>
        {alert ? (
          <p
            role="alert"
            className="border-t border-bp-border-strong px-4 py-2 text-[0.75rem] leading-tight text-bp-danger"
          >
            {alert}
          </p>
        ) : null}
      </div>
    </div>,
    document.body,
  )
}
